import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { type SxProps, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField.tsx';
import type { ChipFilterValue, FiltersConfig } from '@openx/types/chipFilters.ts';
import { type ChangeEvent, type MouseEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChipContainer } from './ChipContainer.tsx';
import { FilterCategoryMenu } from './FilterCategoryMenu.tsx';
import { FilterItemsMenu } from './FilterItemsMenu.tsx';

const StyledAddNewFilterTypography = styled(Typography)`
  cursor: pointer;
  margin-left: ${({ theme }) => theme.spacing(1)};
  white-space: nowrap;
  line-height: 32px;
` as typeof Typography;

const StyledFilterContainerBox = styled(Box)`
  flex: 1;
`;

// Keeps the legacy look on the grey bar: the design-system switch drops the gap, the 13px text and the visible track.
const showMySwitchLabelSx: SxProps = {
  marginRight: '16px',
  '& .MuiFormControlLabel-label': { fontSize: 13 },
  '& .MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled) + .MuiSwitch-track': {
    backgroundColor: 'action.disabled',
  },
};

const StyledFilterIconButton = styled(IconButton)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const StyledFilterContainerDiv = styled('div')`
  display: flex;
  align-items: flex-start;
`;

interface ChipFiltersProps<FiltersValue extends {}> {
  filtersConfig: FiltersConfig;
  filters: FiltersValue;
  onFilterChange: (newFilters: { [categoryName: string]: ChipFilterValue }) => void;
}

export function ChipFiltersView<FiltersValue extends {}>({
  filtersConfig,
  onFilterChange,
  filters,
}: ChipFiltersProps<FiltersValue>) {
  const { t } = useTranslation();
  const filterTextRef = useRef(null);
  const { myDeals, rest: chipFiltersConfig } = useMemo(() => {
    const { myDeals, ...rest } = filtersConfig;
    return { myDeals, rest };
  }, [filtersConfig]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [showMyDeals, setShowMyDeals] = useState<boolean>(false);

  const clearAllFilters = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setShowMyDeals(false);
    onFilterChange(
      Object.keys(chipFiltersConfig).reduce((acc, categoryName) => {
        acc[categoryName] = null;
        return acc;
      }, {})
    );
  };

  const deleteFilter = categoryName => {
    if (categoryName === 'createdBy') {
      setShowMyDeals(false);
    }
    onFilterChange({ [categoryName]: null });
  };

  const editFilter = (element: HTMLElement, category: string) => {
    setAnchorEl(element);
    setCurrentCategory(category);
  };

  const openFiltersClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeFilterWindow = () => {
    setAnchorEl(null);
    setCurrentCategory(null);
  };

  const selectFilterCategory = (categoryName: string) => {
    setCurrentCategory(categoryName);
    const { fetchOptions } = chipFiltersConfig[categoryName];
    fetchOptions?.();
  };

  const applyFilter = (categoryName: string, value: ChipFilterValue): void => {
    onFilterChange({ [categoryName]: value });
    closeFilterWindow();
  };

  const onSwitchChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setShowMyDeals(checked);
    if (checked) {
      onFilterChange({ createdBy: myDeals.options?.[0].value ?? '' });
      return;
    }

    onFilterChange({ createdBy: null });
  };

  const activeFilters: FiltersValue = useMemo((): FiltersValue => {
    return Object.entries(filters).reduce((acc, [categoryName, value]) => {
      if (value && categoryName in chipFiltersConfig) {
        acc[categoryName] = value;
      }
      return acc;
    }, {} as FiltersValue);
  }, [filters, chipFiltersConfig]);

  const isAnyFilterActive = !!Object.keys(activeFilters).length;

  return (
    <StyledFilterContainerDiv>
      <StyledFilterIconButton
        aria-label="Add new filter"
        data-test="filter-icon-button"
        onClick={openFiltersClick}
        size="large"
        sx={{ zIndex: 10 }}
      >
        <FilterListIcon />
      </StyledFilterIconButton>
      <StyledFilterContainerBox>
        {myDeals && (
          <SwitchField
            label={myDeals.name}
            type="checkbox"
            onChange={onSwitchChange}
            checked={showMyDeals}
            formControlLabelSx={showMySwitchLabelSx}
            data-test="show-my-deals"
          />
        )}
        <ChipContainer
          filters={activeFilters}
          filtersConfig={chipFiltersConfig}
          onEdit={editFilter}
          onDelete={deleteFilter}
        />
        <StyledAddNewFilterTypography
          data-test="filter-button"
          ref={filterTextRef}
          variant="body1"
          component="span"
          color="textSecondary"
          onClick={() => {
            !anchorEl && filterTextRef && setAnchorEl(filterTextRef.current);
          }}
        >
          {t('Add new filter')}
        </StyledAddNewFilterTypography>
        <FilterCategoryMenu
          filtersConfig={chipFiltersConfig}
          anchorEl={anchorEl}
          onSelect={selectFilterCategory}
          onClose={closeFilterWindow}
          open={Boolean(anchorEl && !currentCategory)}
          activeFilters={activeFilters}
        />
        {currentCategory && (
          <FilterItemsMenu
            category={currentCategory}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            currentValue={filters[currentCategory]}
            onApplyFilter={applyFilter}
            onClose={closeFilterWindow}
            filterConfig={chipFiltersConfig[currentCategory]}
          />
        )}
      </StyledFilterContainerBox>

      {isAnyFilterActive && (
        <IconButton onClick={clearAllFilters} data-test="clean-filters-button" size="large">
          <CloseIcon />
        </IconButton>
      )}
    </StyledFilterContainerDiv>
  );
}
